import * as ngCore from '@angular/core'

/**
 * Recursive collapsible JSON tree viewer.
 *
 * Renders primitives inline with type-colored spans and wraps objects/arrays
 * in native `<details>` elements so each node can be expanded/collapsed
 * independently. Top-level container is expanded by default; nested nodes are
 * collapsed by default to keep the initial render compact for large payloads.
 */
@ngCore.Component({
  selector: 'atn-json-tree',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** The value to render. Any JSON-shaped data is accepted. */
  public readonly dataSg = ngCore.input.required<unknown>({ alias: 'data' })

  /**
   * Whether the root container `<details>` starts open. Nested containers
   * always start closed regardless of this flag.
   */
  public readonly openSg = ngCore.input<boolean>(true, { alias: 'open' })

  /** Computed view of the data: classifies it for the template switch. */
  public readonly nodeSg = ngCore.computed(() => _classify(this.dataSg()))

  /**
   * Brief "Copied!" indicator state — flipped to `true` for ~1.2s after a
   * successful clipboard write so the user gets visual confirmation. Keyed
   * per-instance because each container in the recursive tree has its own
   * copy button.
   */
  public readonly justCopiedSg = ngCore.signal(false)

  private _copyResetTimeout: ReturnType<typeof setTimeout> | undefined

  /**
   * Copy this subtree to the clipboard as pretty-printed JSON. Stops the
   * click from bubbling to the parent `<summary>` so toggling the disclosure
   * isn't triggered by the copy button.
   */
  public copyToClipboard(event: MouseEvent): void {
    event.preventDefault()
    event.stopPropagation()
    let text: string
    try {
      text = JSON.stringify(this.dataSg(), null, 2)
    } catch {
      text = String(this.dataSg())
    }
    void navigator.clipboard
      .writeText(text)
      .then(() => {
        this.justCopiedSg.set(true)
        if (this._copyResetTimeout) {
          clearTimeout(this._copyResetTimeout)
        }
        this._copyResetTimeout = setTimeout(() => {
          this.justCopiedSg.set(false)
        }, 1200)
      })
      .catch(() => {
        // Best-effort — silently no-op if the browser blocks clipboard access.
      })
  }
}

/**
 * Classification of a JSON value so the template can `@switch` on `kind`
 * without re-deriving the type on every check.
 */
export type Node =
  | { kind: 'null' }
  | { kind: 'boolean'; value: boolean }
  | { kind: 'number'; value: number }
  | { kind: 'string'; value: string }
  | { kind: 'array'; items: readonly unknown[] }
  | { kind: 'object'; entries: readonly { key: string; value: unknown }[] }
  | { kind: 'other'; text: string }

/**
 * Classifies an arbitrary value into a {@link Node} for rendering. Objects
 * and arrays are passed through as-is so the recursive child component can
 * re-classify each entry on its own — keeps the data immutable per level.
 */
function _classify(value: unknown): Node {
  if (value === null || value === undefined) {
    return { kind: 'null' }
  }
  if (typeof value === 'boolean') {
    return { kind: 'boolean', value }
  }
  if (typeof value === 'number') {
    return { kind: 'number', value }
  }
  if (typeof value === 'string') {
    return { kind: 'string', value }
  }
  if (Array.isArray(value)) {
    return { kind: 'array', items: value }
  }
  if (typeof value === 'object') {
    return {
      kind: 'object',
      entries: Object.entries(value as Record<string, unknown>).map(
        ([key, val]) => ({ key, value: val }),
      ),
    }
  }
  return { kind: 'other', text: String(value) }
}
